import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Checkbox,
  FormGroup,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, OpenInNew as OpenInNewIcon, WhatsApp as WhatsAppIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { buildCrmApiUrl } from '../../api/apiConfig';
import { crm, copyText, fmtDateTime, fromNow } from '../../api/crm';
import { useSales } from '../../context/SalesContext';

// How long a generated link stays payable (minutes). Razorpay allows 20 minutes to 7 days.
const EXPIRY_OPTIONS = [
  { minutes: 60, label: '1 hour' },
  { minutes: 360, label: '6 hours' },
  { minutes: 1440, label: '24 hours' },
  { minutes: 4320, label: '3 days' },
  { minutes: 10080, label: '7 days' },
  { minutes: -1, label: 'Custom' },
];

const whatsappUrl = (number, link) => `https://wa.me/91${number}?text=${encodeURIComponent(`Here is your payment link: ${link}`)}`;

const RESEARCH_PLAN_ASSETS = ['Stock', 'Option', 'Future', 'Multibagger'];

const PaymentLinks = () => {
  const theme = useTheme();
  const sales = useSales();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [result, setResult] = useState(null);

  const [rplans, setRplans] = useState([]);
  const [rplansLoading, setRplansLoading] = useState(false);
  const [algoPlans, setAlgoPlans] = useState([]);
  const [algoPlansLoading, setAlgoPlansLoading] = useState(false);

  const [type, setType] = useState('algo'); // 'algo' | 'research'
  const [paymentType, setPaymentType] = useState('single'); // 'single' | 'recurring'
  const [researchMode, setResearchMode] = useState('plan'); // 'plan' | 'assets'

  // Opened from a lead ("Payment link" action): the customer is filled in.
  const [name, setName] = useState(searchParams.get('name') || '');
  const [number, setNumber] = useState((searchParams.get('number') || '').replace(/\D/g, '').slice(-10));
  const [expiryChoice, setExpiryChoice] = useState(1440);
  const [customExpiryHours, setCustomExpiryHours] = useState('');

  // Links this person generated that are still unpaid and unexpired.
  const [activeLinks, setActiveLinks] = useState([]);
  const [activeTotal, setActiveTotal] = useState(0);
  const [activeLimit, setActiveLimit] = useState(20);
  const [activeScope, setActiveScope] = useState('mine');
  const [activeLoading, setActiveLoading] = useState(false);

  const loadActiveLinks = async () => {
    setActiveLoading(true);
    try {
      const data = await crm('payment-links/active', { query: { limit: activeLimit, scope: activeScope === 'team' ? 'team' : '' } });
      setActiveLinks(Array.isArray(data?.data) ? data.data : []);
      setActiveTotal(data?.total || 0);
    } catch (e) {
      setActiveLinks([]);
      setActiveTotal(0);
    } finally {
      setActiveLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadActiveLinks(); }, [activeLimit, activeScope]);
  const [amount, setAmount] = useState('');
  const [authorizationAmount, setAuthorizationAmount] = useState('');
  const [accessMonths, setAccessMonths] = useState(1);
  const [trial, setTrial] = useState(false);

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [selectedAlgoPlanId, setSelectedAlgoPlanId] = useState('');
  const [selectedAlgoPlanName, setSelectedAlgoPlanName] = useState('');
  const [selectedResearchAssets, setSelectedResearchAssets] = useState([]);

  const num = localStorage.getItem('number');

  // Who gets the revenue credit: an admin picks anyone, a leader may generate on behalf
  // of a team member, everyone else is credited themselves (the backend enforces this).
  const role = sales.role;
  const owners = (sales.team || []).filter((m) => m.active);
  const canPickOwner = sales.isAdmin || (sales.isLeader && owners.length > 1);
  const [selectedOwner, setSelectedOwner] = useState('');

  // Research supports single payment only; keep the state consistent when switching type.
  useEffect(() => {
    if (type === 'research') setPaymentType('single');
  }, [type]);

  useEffect(() => {
    if (type !== 'research' || researchMode !== 'plan' || !num) return;
    setRplansLoading(true);
    fetch(buildCrmApiUrl(`payment-links/rplans?number=${encodeURIComponent(num)}`), {
      headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' }
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.data)) {
          setRplans(d.data);
          if (d.data.length > 0 && !selectedPlanId) {
            setSelectedPlanId(d.data[0].plan_id);
            setSelectedPlanName(d.data[0].name);
          }
        }
      })
      .catch(() => setSnackbar({ open: true, message: 'Failed to load research plans', severity: 'error' }))
      .finally(() => setRplansLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, researchMode]);

  useEffect(() => {
    if (type !== 'algo' || !num) return;
    setAlgoPlansLoading(true);
    fetch(buildCrmApiUrl(`payment-links/algo-plans?number=${encodeURIComponent(num)}`), {
      headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' }
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.data)) {
          setAlgoPlans(d.data);
          if (d.data.length > 0 && !selectedAlgoPlanId) {
            setSelectedAlgoPlanId(d.data[0].plan_id);
            setSelectedAlgoPlanName(d.data[0].name);
          }
        }
      })
      .catch(() => setSnackbar({ open: true, message: 'Failed to load algo plans', severity: 'error' }))
      .finally(() => setAlgoPlansLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Algo price limits for the selected plan/months/customer (normal, minimum after any
  // upgrade credit, and the owner's paid-discount quota). The backend enforces them.
  const [algoLimits, setAlgoLimits] = useState(null);
  useEffect(() => {
    const months = parseInt(accessMonths, 10);
    if (type !== 'algo' || !num || !selectedAlgoPlanId || !(months >= 1)) {
      setAlgoLimits(null);
      return;
    }
    const customer = number.length === 10 ? number : '';
    const t = setTimeout(() => {
      fetch(buildCrmApiUrl(`payment-links/algo-limits?number=${encodeURIComponent(num)}&algo_plan_id=${encodeURIComponent(selectedAlgoPlanId)}&access_months=${months}&customer=${encodeURIComponent(customer)}`), {
        headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' }
      })
        .then((r) => r.json())
        .then((d) => setAlgoLimits(d?.success ? d.data : null))
        .catch(() => setAlgoLimits(null));
    }, 300);
    return () => clearTimeout(t);
  }, [type, num, selectedAlgoPlanId, accessMonths, number]);

  // Dealer context: is the customer a dealer's client, and what was the last link anyone
  // (a dealer or sales) sent them? Shown before billing so nobody undercuts a dealer;
  // the backend refuses a link for such a customer until "seen" is ticked.
  const [dealerCtx, setDealerCtx] = useState(null);
  const [ackDealer, setAckDealer] = useState(false);
  useEffect(() => {
    setAckDealer(false);
    if (!num || number.length !== 10) {
      setDealerCtx(null);
      return;
    }
    const t = setTimeout(() => {
      fetch(buildCrmApiUrl(`payment-links/last?number=${encodeURIComponent(num)}&customer=${encodeURIComponent(number)}`), {
        headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' }
      })
        .then((r) => r.json())
        .then((d) => setDealerCtx(d?.success ? d.data : null))
        .catch(() => setDealerCtx(null));
    }, 300);
    return () => clearTimeout(t);
  }, [num, number]);

  const inr = (v) => `₹${Math.round(v).toLocaleString('en-IN')}`;
  const linkLine = (l) => `${l.by_type === 'dealer' ? 'Dealer' : 'Sales'} ${l.by_name || ''} sent ${inr(l.amount)}` +
    `${l.algo_plan_id ? ` · ${l.algo_plan_id.toUpperCase()}` : l.plan_name ? ` · ${l.plan_name}` : ''}` +
    `${l.access_months ? ` · ${l.access_months} months` : ''} · ${fmtDateTime(l.created_at)}${l.paid ? ' · PAID' : ''}`;
  const dealerBlocks = !!dealerCtx && (!!dealerCtx.dealer_name || !!dealerCtx.last_dealer_link);
  const renderDealer = () => {
    if (!dealerCtx || (!dealerCtx.dealer_name && !dealerCtx.last_link && !dealerCtx.last_dealer_link)) return null;
    const dl = dealerCtx.last_dealer_link;
    const last = dealerCtx.last_link;
    return (
      <Alert severity={dealerBlocks ? 'warning' : 'info'} sx={{ mt: 2, maxWidth: 700 }}>
        {dealerCtx.dealer_name && <div><b>Dealer client:</b> {dealerCtx.dealer_name}</div>}
        {dl && <div><b>Dealer's link:</b> {linkLine(dl)}</div>}
        {last && (!dl || last.created_at !== dl.created_at) && <div><b>Last link:</b> {linkLine(last)}</div>}
        {dealerBlocks && (
          <>
            <div style={{ fontSize: 12, marginTop: 4 }}>Do not offer this customer a lower price than the dealer did.</div>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={<Checkbox size="small" checked={ackDealer} onChange={(e) => setAckDealer(e.target.checked)} />}
              label={<Typography variant="body2">I have seen the dealer's offer</Typography>}
            />
          </>
        )}
      </Alert>
    );
  };
  const renderAlgoLimits = () => {
    if (!algoLimits) return null;
    const amt = parseFloat(amount);
    let severity = 'info';
    let verdict = '';
    if (!algoLimits.is_admin && !isNaN(amt) && amt > 0) {
      if (amt < algoLimits.minimum) {
        severity = 'error';
        verdict = 'Below your minimum for this plan.';
      } else if (amt < algoLimits.normal) {
        const left = algoLimits.quota_limit - algoLimits.quota_used;
        if (!algoLimits.can_discount) {
          severity = 'error';
          verdict = 'You are not allowed to discount. Ask your team leader.';
        } else {
          severity = left > 0 ? 'warning' : 'error';
          verdict = left > 0
            ? `Discounted: uses 1 of your ${algoLimits.quota_limit} monthly discounts once paid (${left} left).`
            : 'Discount quota used up for this month.';
        }
      } else {
        severity = 'success';
        verdict = 'At or above normal price.';
      }
    }
    return (
      <Alert severity={severity} sx={{ mt: 2, maxWidth: 700 }}>
        <div>
          Normal {inr(algoLimits.normal)} · Minimum {inr(algoLimits.minimum)} for {algoLimits.access_months} month(s)
          {algoLimits.upgrade_credit > 0 && ` (list ${inr(algoLimits.list_normal)} / ${inr(algoLimits.list_minimum)} − ${inr(algoLimits.upgrade_credit)} upgrade credit)`}
        </div>
        {(algoLimits.credit_details || []).map((c) => <div key={c} style={{ fontSize: 12 }}>{c}</div>)}
        {!algoLimits.is_admin && (
          <div style={{ fontSize: 12 }}>Paid discounted links this month: {algoLimits.quota_used}/{algoLimits.quota_limit}</div>
        )}
        {algoLimits.is_admin && <div style={{ fontSize: 12 }}>Admin: limits not enforced.</div>}
        {verdict && <div style={{ fontWeight: 600 }}>{verdict}</div>}
      </Alert>
    );
  };

  // Wrong-plan check: sales often pick one plan and type another plan's price (Starter
  // selected, ₹19,999 charged). Anything above 2× the selected plan's list price for
  // these months, or below half its minimum, is flagged. The plans are 3–10× apart per
  // month, so a real price for the selected plan never trips it. Warning only.
  const planMismatch = () => {
    if (type !== 'algo' || !algoLimits || !(algoLimits.plans || []).length) return null;
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return null;
    const sel = algoLimits.plans.find((p) => p.algo_plan_id === algoLimits.algo_plan_id);
    if (!sel) return null;
    const high = amt > 2 * algoLimits.list_normal;
    const low = amt < 0.5 * algoLimits.minimum; // after any upgrade credit
    if (!high && !low) return null;
    const fits = algoLimits.plans.filter((p) => amt >= 0.9 * p.minimum && amt <= 1.5 * p.normal);
    const other = fits.find((p) => p.algo_plan_id !== sel.algo_plan_id);
    const fullPeriod = (p) => `${p.name} is ${inr(p.minimum)}–${inr(p.normal)} for ${p.period_months} months`;
    let hint;
    if (other) {
      hint = `This amount looks like ${other.name}, not ${sel.name}. ${fullPeriod(other)}.`;
    } else if (fits.length) {
      hint = `This looks like the ${sel.period_months}-month ${sel.name} price — check Access months (now ${algoLimits.access_months}).`;
    } else {
      hint = `${fullPeriod(sel)}.`;
    }
    return {
      title: `${inr(amt)} is ${high ? 'far above' : 'far below'} the ${sel.name} price for ${algoLimits.access_months} month(s) (${inr(algoLimits.list_minimum)}–${inr(algoLimits.list_normal)}).`,
      hint
    };
  };
  const mismatch = planMismatch();
  const [pendingPayload, setPendingPayload] = useState(null);

  const handleResearchAssetToggle = (asset) => {
    setSelectedResearchAssets((prev) =>
      prev.includes(asset) ? prev.filter((a) => a !== asset) : [...prev, asset]
    );
  };

  const handlePlanSelect = (e) => {
    const planId = e.target.value;
    const plan = rplans.find((p) => p.plan_id === planId);
    setSelectedPlanId(planId);
    setSelectedPlanName(plan ? plan.name : '');
  };

  const handleAlgoPlanSelect = (e) => {
    const planId = e.target.value;
    const plan = algoPlans.find((p) => p.plan_id === planId);
    setSelectedAlgoPlanId(planId);
    setSelectedAlgoPlanName(plan ? plan.name : '');
  };

  const copy = async (link) => {
    if (!link) return;
    const ok = await copyText(link);
    setSnackbar({ open: true, message: ok ? 'Link copied' : 'Could not copy. Long-press the link to copy it.', severity: ok ? 'success' : 'warning' });
  };
  const copyLink = () => copy(result?.payment_link);

  const expiryMinutes = () => {
    if (expiryChoice !== -1) return expiryChoice;
    const hours = parseFloat(customExpiryHours);
    return isNaN(hours) ? 0 : Math.round(hours * 60);
  };

  const handleSubmit = async () => {
    if (!num) {
      setSnackbar({ open: true, message: 'Please log in', severity: 'error' });
      return;
    }
    const cleanNumber = (number || '').replace(/\D/g, '').slice(-10);
    if (cleanNumber.length !== 10) {
      setSnackbar({ open: true, message: 'Enter a 10-digit mobile number', severity: 'error' });
      return;
    }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      setSnackbar({ open: true, message: 'Amount must be greater than 0', severity: 'error' });
      return;
    }
    const months = parseInt(accessMonths, 10);
    if (isNaN(months) || months < 1) {
      setSnackbar({ open: true, message: 'Access months must be at least 1', severity: 'error' });
      return;
    }

    const expiry = expiryMinutes();
    if (expiry < 20 || expiry > 10080) {
      setSnackbar({ open: true, message: 'Link expiry must be between 20 minutes and 7 days', severity: 'error' });
      return;
    }

    const payload = {
      type,
      payment_type: type === 'research' ? 'single' : paymentType,
      number: cleanNumber,
      name: name.trim(),
      amount: amt,
      access_months: months,
      expiry_minutes: expiry,
      ack_dealer: ackDealer
    };

    // Admin: any owner. Leader: a member of their team. Otherwise credited to the caller.
    if (canPickOwner && selectedOwner) {
      payload.owner = selectedOwner;
    }

    if (type === 'research') {
      if (researchMode === 'plan') {
        if (!selectedPlanId || !selectedPlanName) {
          setSnackbar({ open: true, message: 'Select a plan', severity: 'error' });
          return;
        }
        payload.plan_id = selectedPlanId;
        payload.plan_name = selectedPlanName;
      } else {
        if (selectedResearchAssets.length === 0) {
          setSnackbar({ open: true, message: 'Select at least one asset', severity: 'error' });
          return;
        }
        payload.plan_assets = selectedResearchAssets;
      }
    } else {
      if (!selectedAlgoPlanId) {
        setSnackbar({ open: true, message: 'Select an algo plan', severity: 'error' });
        return;
      }
      // A single algo plan drives everything: it is both the algo_plan_id and the
      // sole plan asset granted on payment.
      payload.algo_plan_id = selectedAlgoPlanId;
      payload.plan_name = selectedAlgoPlanName;
      payload.plan_assets = [selectedAlgoPlanId];

      if (paymentType === 'recurring') {
        payload.trial = trial;
        const authAmt = parseFloat(authorizationAmount);
        payload.authorization_amount = isNaN(authAmt) ? 0 : authAmt;
      }
    }

    if (mismatch) {
      setPendingPayload(payload);
      return;
    }
    send(payload);
  };

  const send = async (payload) => {
    setPendingPayload(null);
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(buildCrmApiUrl(`payment-links/create?number=${encodeURIComponent(num)}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'dskjhvfgfg46953u0y5y50y90y'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.data);
        setSnackbar({ open: true, message: data.data?.message || 'Payment link generated', severity: 'success' });
        loadActiveLinks();
      } else {
        setSnackbar({ open: true, message: data.error || 'Failed to generate payment link', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Request failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const isAlgoRecurring = type === 'algo' && paymentType === 'recurring';

  return (
    <Box sx={{ p: { xs: 0, sm: 2 } }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Payment Links
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate a Razorpay payment link for research or algo plans. Access is granted automatically once the
        customer pays.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 3 },
          borderRadius: 3,
          border: 1, borderColor: 'divider'
        }}
      >
        <Segmented label="Plan type" value={type} onChange={setType} options={[['algo', 'Algo'], ['research', 'Research']]} />

        {type === 'algo' && (
          <Segmented label="Payment" value={paymentType} onChange={setPaymentType} options={[['single', 'One-time'], ['recurring', 'Recurring (UPI Autopay)']]} />
        )}

        {type === 'research' && (
          <Segmented label="Sell" value={researchMode} onChange={setResearchMode} options={[['plan', 'A plan'], ['assets', 'Individual assets']]} />
        )}

        {type === 'research' && researchMode === 'plan' && (
          <FormControl fullWidth sx={{ mb: 2, maxWidth: 500 }}>
            <InputLabel>Plan</InputLabel>
            <Select value={selectedPlanId} label="Plan" onChange={handlePlanSelect} disabled={rplansLoading}>
              {rplansLoading && <MenuItem value="">Loading...</MenuItem>}
              {rplans.map((p) => (
                <MenuItem key={p.plan_id} value={p.plan_id}>
                  {p.name} ({p.plan_id?.slice(0, 12)}...)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {type === 'research' && researchMode === 'assets' && (
          <Box sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ mb: 1, display: 'block' }}>
              Select Assets (multiple)
            </FormLabel>
            <FormGroup row>
              {RESEARCH_PLAN_ASSETS.map((asset) => (
                <FormControlLabel
                  key={asset}
                  control={
                    <Checkbox
                      checked={selectedResearchAssets.includes(asset)}
                      onChange={() => handleResearchAssetToggle(asset)}
                    />
                  }
                  label={asset}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {type === 'algo' && (
          <FormControl fullWidth sx={{ mb: 2, maxWidth: 500 }}>
            <InputLabel>Algo Plan</InputLabel>
            <Select
              value={selectedAlgoPlanId}
              label="Algo Plan"
              onChange={handleAlgoPlanSelect}
              disabled={algoPlansLoading}
            >
              {algoPlansLoading && <MenuItem value="">Loading...</MenuItem>}
              {algoPlans.map((p) => (
                <MenuItem key={p.plan_id} value={p.plan_id}>
                  {p.name} ({p.plan_id})
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Granted as the plan asset and stored as algo_plan_id</FormHelperText>
          </FormControl>
        )}

        {isAlgoRecurring && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={trial} onChange={(e) => setTrial(e.target.checked)} />}
              label="Trial (₹5 mandate authorization)"
            />
          </Box>
        )}

        {canPickOwner && (
          <FormControl fullWidth sx={{ mb: 2, maxWidth: 500 }}>
            <InputLabel>Credit revenue to</InputLabel>
            <Select
              value={selectedOwner}
              label="Credit revenue to"
              onChange={(e) => setSelectedOwner(e.target.value)}
            >
              <MenuItem value=""><em>{role === 'admin' ? 'Unassigned (default attribution)' : `Me (${sales.owner})`}</em></MenuItem>
              {owners.filter((o) => o.owner !== sales.owner).map((o) => (
                <MenuItem key={`${o.owner}-${o.number}`} value={o.owner}>
                  {o.owner}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>The generated link's revenue is credited to this person.</FormHelperText>
          </FormControl>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, maxWidth: { xs: '100%', sm: 500 } }}>
          <TextField
            fullWidth
            label="Customer Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Shown on the Razorpay link"
          />
          {/* Digits only: numbers pasted from iPhone Contacts / WhatsApp carry invisible
              Unicode direction marks that would otherwise be stored as part of the number. */}
          <TextField
            fullWidth
            label="Mobile Number"
            value={number}
            onChange={(e) => setNumber(e.target.value.replace(/\D/g, '').slice(-10))}
            inputProps={{ inputMode: 'numeric', maxLength: 20 }}
            placeholder="e.g. 8879102860"
            helperText={number && number.length !== 10 ? 'Enter a 10-digit mobile number' : ' '}
            error={Boolean(number) && number.length !== 10}
          />
          <TextField
            fullWidth
            type="number"
            label={isAlgoRecurring ? 'Amount (₹) — charged every cycle' : 'Amount (₹)'}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
          />
          {isAlgoRecurring && (
            <TextField
              fullWidth
              type="number"
              label="Authorization Amount (₹)"
              value={trial ? 5 : authorizationAmount}
              onChange={(e) => setAuthorizationAmount(e.target.value)}
              disabled={trial}
              helperText={
                trial
                  ? 'Trial forces a ₹5 authorization debit'
                  : 'Debited to authorize the mandate. Leave blank to debit the full amount.'
              }
              inputProps={{ min: 0, step: 0.01 }}
            />
          )}
          <TextField
            fullWidth
            type="number"
            label="Access Months"
            value={accessMonths}
            onChange={(e) => setAccessMonths(e.target.value)}
            inputProps={{ min: 1, inputMode: 'numeric' }}
          />
          <TextField
            select
            fullWidth
            label="Link expires in"
            value={expiryChoice}
            onChange={(e) => setExpiryChoice(Number(e.target.value))}
            helperText="After this the customer can no longer pay with the link"
          >
            {EXPIRY_OPTIONS.map((o) => <MenuItem key={o.minutes} value={o.minutes}>{o.label}</MenuItem>)}
          </TextField>
          {expiryChoice === -1 && (
            <TextField
              fullWidth
              type="number"
              label="Expiry (hours)"
              value={customExpiryHours}
              onChange={(e) => setCustomExpiryHours(e.target.value)}
              helperText="From 0.5 to 168 hours (7 days)"
              inputProps={{ min: 0.5, max: 168, step: 0.5, inputMode: 'decimal' }}
            />
          )}
        </Box>

        {renderDealer()}
        {type === 'algo' && renderAlgoLimits()}
        {mismatch && (
          <Alert severity="warning" sx={{ mt: 2, maxWidth: 700 }}>
            <div style={{ fontWeight: 600 }}>Wrong plan? {mismatch.title}</div>
            <div>{mismatch.hint}</div>
          </Alert>
        )}

        <Dialog open={!!pendingPayload} onClose={() => setPendingPayload(null)}>
          <DialogTitle>Check the plan before creating</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Plan: <b>{selectedAlgoPlanName || selectedAlgoPlanId}</b> · {accessMonths} month(s) · Amount: <b>{inr(parseFloat(amount) || 0)}</b>
            </Typography>
            {mismatch && <Typography gutterBottom>{mismatch.title}</Typography>}
            {mismatch && <Typography fontWeight={600}>{mismatch.hint}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingPayload(null)}>Go back and fix</Button>
            <Button color="warning" variant="contained" onClick={() => send(pendingPayload)}>Create anyway</Button>
          </DialogActions>
        </Dialog>

        {/* On a phone the button stays in reach at the bottom of the screen. */}
        <Box sx={{
          mt: 3,
          position: { xs: 'sticky', sm: 'static' },
          bottom: { xs: 8 },
          zIndex: 5,
          bgcolor: { xs: 'background.paper', sm: 'transparent' },
          py: { xs: 1, sm: 0 }
        }}>
          <Button variant="contained" size="large" onClick={handleSubmit} disabled={loading} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {loading ? <CircularProgress size={24} /> : 'Generate Payment Link'}
          </Button>
        </Box>
      </Paper>

      {result && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
            bgcolor: alpha(theme.palette.success.main, 0.04)
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Payment Link
            </Typography>
            <Chip size="small" label={result.type} />
            <Chip
              size="small"
              color={result.payment_type === 'recurring' ? 'primary' : 'default'}
              label={result.payment_type === 'recurring' ? 'Recurring / Registration' : 'Single Payment'}
            />
          </Box>

          <TextField
            fullWidth
            value={result.payment_link || ''}
            InputProps={{ readOnly: true }}
            size="small"
            onFocus={(e) => e.target.select()}
            sx={{ maxWidth: 560, mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={copyLink} sx={{ flex: { xs: '1 1 45%', sm: '0 0 auto' } }}>Copy</Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<WhatsAppIcon />}
              component="a"
              href={whatsappUrl(result.number, result.payment_link)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
            >
              WhatsApp
            </Button>
            <Button variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={result.payment_link} target="_blank" rel="noopener noreferrer">Open</Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1.5 }}>
            <Detail label="Number" value={result.number} />
            <Detail label="Amount" value={`₹${result.amount}`} />
            <Detail label="Order ID" value={result.order_id} />
            <Detail label="Customer ID" value={result.customer_id || '—'} />
            <Detail label="Invoice ID" value={result.invoice_id} />
            <Detail label="Link expires" value={fmtDateTime(result.expires_at)} />
            <Detail label="Credited to" value={result.owner || '—'} />
            <Detail label="Transaction ID" value={`${String(result.transaction_id || '').slice(0, 24)}...`} />
          </Box>
        </Paper>
      )}

      {/* Active links: unpaid and not yet expired. Paid and expired links drop off. */}
      <Paper elevation={0} sx={{ mt: 3, p: { xs: 1.5, sm: 3 }, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
            Active links {activeTotal > 0 && `(${activeTotal})`}
          </Typography>
          {sales.isLeader && (
            <Select size="small" value={activeScope} onChange={(e) => { setActiveScope(e.target.value); setActiveLimit(20); }}>
              <MenuItem value="mine">Sent by me</MenuItem>
              <MenuItem value="team">Sent by my team</MenuItem>
            </Select>
          )}
          <IconButton size="small" onClick={loadActiveLinks} title="Refresh">
            {activeLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {sales.isAdmin ? 'Every unpaid, unexpired link.' : 'Links you generated that are still unpaid and not expired.'}
        </Typography>
        {activeLinks.length === 0 && !activeLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No active links.</Typography>
        )}
        {activeLinks.map((l) => (
          <Box key={l.transaction_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
            <Box sx={{ flexGrow: 1, minWidth: 160 }}>
              <Typography variant="body2" fontWeight={600}>
                {l.number} · ₹{Math.round(l.amount).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {[l.plan, `${l.access_months} mo`, l.recurring ? 'recurring' : '', (sales.isManager && l.owner) || ''].filter(Boolean).join(' · ')}
              </Typography>
              <Typography variant="caption" color="warning.main" component="div">
                Expires {fromNow(l.link_expires_at)} ({fmtDateTime(l.link_expires_at)})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" onClick={() => copy(l.link_url)} title="Copy link" disabled={!l.link_url}><ContentCopyIcon fontSize="small" /></IconButton>
              <IconButton size="small" component="a" href={whatsappUrl(l.number, l.link_url)} target="_blank" rel="noopener noreferrer" title="Send on WhatsApp" disabled={!l.link_url} sx={{ color: '#25D366' }}><WhatsAppIcon fontSize="small" /></IconButton>
              <IconButton size="small" component="a" href={l.link_url} target="_blank" rel="noopener noreferrer" title="Open link" disabled={!l.link_url}><OpenInNewIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
        ))}
        {activeTotal > activeLinks.length && activeLimit < 50 && (
          <Button size="small" onClick={() => setActiveLimit(50)} sx={{ mt: 1 }}>Show more</Button>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Segmented: a labelled either/or switch (plan type, payment type, ...).
const Segmented = ({ label, value, onChange, options }) => (
  <Box sx={{ mb: 2, display: 'inline-flex', flexDirection: 'column', gap: 0.5, mr: 3, verticalAlign: 'top', maxWidth: '100%' }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
    <ToggleButtonGroup exclusive size="small" value={value} onChange={(_, v) => v && onChange(v)} sx={{ flexWrap: 'wrap' }}>
      {options.map(([v, l]) => <ToggleButton key={v} value={v} sx={{ px: 2 }}>{l}</ToggleButton>)}
    </ToggleButtonGroup>
  </Box>
);

const Detail = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Box>
);

export default PaymentLinks;
